<template>
  <v-card class="conv-card">
    <!-- Mobile toolbar -->
    <v-toolbar flat light color="white" class="conv-toolbar" v-if="$vuetify.breakpoint.smAndDown">
      <v-toolbar-title>{{ $t('messenger.conversationTitle') }} {{ conversation.endedAt | formatDate }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>fa-times</v-icon>
      </v-btn>
    </v-toolbar>
    <v-divider v-if="$vuetify.breakpoint.smAndDown"></v-divider>

    <!-- Desktop: close button + title -->
    <div v-if="$vuetify.breakpoint.mdAndUp" class="conv-header">
      <div class="d-flex justify-end px-4 pt-2">
        <v-btn color="color-grey" depressed text small @click="$emit('close')">
          <v-icon class="mr-3">fa-times</v-icon>
          <span>{{ $t('close') }}</span>
        </v-btn>
      </div>
      <div class="text-center px-4 pb-3">
        <h2 class="text-h2 mt-6 mb-2">{{ $t('messenger.conversationTitle') }} {{ conversation.endedAt | formatDate }}</h2>
        <p class="text-body-2 color-new-title-grey--text mb-0">{{ $t('messenger.conversationClosedInfo') }}</p>
      </div>
    </div>

    <!-- Scrollable messages (scrollable:true dialog makes v-card-text fill + scroll) -->
    <v-card-text class="conv-messages-area pa-0" ref="messageList">
      <div class="content-wrap">
        <template v-for="item in messagesWithDates">
          <div v-if="item.type === 'date-separator'" :key="'sep-' + item.id" class="date-separator">
            <span class="date-separator-label">{{ item.date | formatDate }}</span>
          </div>
          <div
            v-else
            :key="'msg-' + item.id"
            :class="['message', item.direction]"
          >
            <div class="message-inner">
              <div v-if="item.direction === 'incoming'" class="msg-header incoming-header">
                <img src="/static/favicons/android-chrome-192x192.png" class="b2-logo" alt="B2 Impact" />
                <span class="msg-sender">{{ item.isBot ? 'B2 Impact Bot' : 'B2 Impact' }}</span>
                <span class="msg-time">{{ item.timestamp | formatTime }}</span>
              </div>
              <div v-else class="msg-header outgoing-header">
                <span class="msg-sender">{{ debtorFirstName }}</span>
                <span class="msg-time">{{ item.timestamp | formatTime }}</span>
              </div>
              <div :class="['bubble', item.direction === 'outgoing' ? 'outgoing-bubble' : 'incoming-bubble']">
                <div v-if="item.attachmentUrl">
                  <span
                    class="attachment-link"
                    :class="{ 'attachment-link--loading': downloadingIds[item.id] }"
                    @click="downloadAttachment(item)"
                  >
                    <v-progress-circular
                      v-if="downloadingIds[item.id]"
                      indeterminate
                      :size="14"
                      :width="2"
                      class="mr-1"
                    />
                    <v-icon v-else small class="mr-1">fas fa-paperclip</v-icon>
                    {{ item.text }}
                  </span>
                </div>
                <div v-else class="bubble-text">{{ item.text }}</div>
              </div>
            </div>
          </div>
        </template>
        <div class="conv-ended-label">{{ $t('messenger.conversationEnded') }} {{ conversation.endedAt | formatTime }}</div>
      </div>
    </v-card-text>

    <div v-if="downloadError" class="download-error">
      <v-icon x-small color="error" class="mr-1">fas fa-exclamation-circle</v-icon>
      {{ $t('messenger.attachmentDownloadError') }}
    </div>

    <v-divider></v-divider>

    <!-- Footer: back button -->
    <v-card-actions class="justify-center pa-4">
      <v-btn tile outlined color="color-new-link-blue" @click="$emit('close')">
        {{ $t('messenger.backToMessages') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { apiResource } from '@/util/resources'

  export default {
    name: 'ConversationHistoryDialog',
    props: {
      conversation: {
        type: Object,
        required: true
      }
    },

    data: function () {
      return {
        downloadError: false,
        downloadingIds: {}
      }
    },
    computed: {
      debtor: function () {
        return this.$store.getters['debtor'] || {}
      },
      debtorFirstName: function () {
        return this.debtor.firstName || ''
      },
      messagesWithDates: function () {
        var result = []
        var lastDateKey = null
        var isFirst = true
        var messages = this.conversation.messages || []
        messages.forEach(function (m) {
          var ts = m.timestamp instanceof Date ? m.timestamp : new Date(m.timestamp)
          var key = ts.getFullYear() + '-' + ts.getMonth() + '-' + ts.getDate()
          if (key !== lastDateKey) {
            lastDateKey = key
            if (!isFirst) {
              result.push({ type: 'date-separator', id: 'date-' + key, date: ts })
            }
            isFirst = false
          }
          result.push(m)
        })
        return result
      }
    },
    watch: {
      conversation: {
        immediate: true,
        handler: function () {
          var self = this
          self.$nextTick(function () {
            var el = self.$refs.messageList
            if (el) el.scrollTop = el.scrollHeight
          })
        }
      }
    },

    methods: {
      downloadAttachment: function (item) {
        if (this.downloadingIds[item.id]) return
        var self = this
        self.downloadError = false
        self.$set(self.downloadingIds, item.id, true)
        apiResource.get('api/messenger/attachment/' + item.id, { responseType: 'blob' })
          .then(function (response) {
            var blobUrl = URL.createObjectURL(response.data)
            var a = document.createElement('a')
            a.href = blobUrl
            a.download = item.text || 'attachment'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(blobUrl)
          })
          .catch(function (err) {
            console.error('[ConversationHistoryDialog] attachment download failed', err)
            self.downloadError = true
          })
          .finally(function () {
            self.$set(self.downloadingIds, item.id, false)
          })
      }
    }
  }
</script>

<style scoped>
  .conv-header {
    background: #fff;
  }

  .conv-toolbar {
    max-height: 56px;
  }

  .conv-header {
    background: #fff;
  }

  .conv-messages-area {
    /* Don't stretch to fill card height — size to content on both mobile and desktop */
    flex: 0 1 auto !important;
    overflow-y: auto;
    background: #F7F8FA;
    padding: 8px 16px 16px;
  }

  .content-wrap {
    max-width: 560px;
    margin-left: auto;
    margin-right: auto;
    border-radius: 4px;
    padding: 16px;
  }

  .message {
    display: flex;
    margin-bottom: 24px;
  }

  .message.incoming {
    justify-content: flex-start;
  }

  .message.outgoing {
    justify-content: flex-end;
  }

  .message-inner {
    display: flex;
    flex-direction: column;
    max-width: 75%;
  }

  .msg-header {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    font-size: 12px;
  }

  .incoming-header {
    justify-content: flex-start;
  }

  .outgoing-header {
    justify-content: flex-end;
  }

  .b2-logo {
    width: 32px;
    height: 32px;
    object-fit: contain;
    border-radius: 4px;
  }

  .msg-sender {
    font-weight: 600;
    /* color-new-dark-grey: #435262 */
    color: #435262;
  }

  .msg-time {
    /* color-grey: #A2ADB8 */
    color: #A2ADB8;
  }

  .bubble {
    padding: 12px 16px;
    border-radius: 10px;
    word-break: break-word;
    line-height: 1.5;
  }

  /* Outgoing bubble — white with primary green border */
  .outgoing-bubble {
    background: #fff;
    border: 2.5px solid #40c6a6;
    /* color-new-body-grey: #3D546C */
    color: #3D546C;
  }

  /* Incoming bubble — color-new-link-blue border: #485cc7 */
  .incoming-bubble {
    background: #fff;
    border: 2.5px solid #485cc7;
    /* color-new-body-grey: #3D546C */
    color: #3D546C;
  }

  .bubble-text {
    word-break: break-word;
    line-height: 1.4;
  }

  .attachment-link {
    display: inline-flex;
    align-items: center;
    word-break: break-all;
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
  }

  .attachment-link--loading {
    cursor: default;
    opacity: 0.65;
  }

  .download-error {
    display: flex;
    align-items: center;
    padding: 4px 12px;
    font-size: 12px;
    color: #e53935;
    background: #fff;
  }

  .conv-ended-label {
    text-align: center;
    font-size: 12px;
    /* color-grey: #A2ADB8 */
    color: #A2ADB8;
    margin-top: 8px;
    padding: 4px 0;
  }

  .date-separator {
    display: flex;
    align-items: center;
    margin: 12px 0;
    gap: 8px;
  }

  .date-separator::before,
  .date-separator::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #D8DCE3;
  }

  .date-separator-label {
    font-size: 11px;
    color: #8A99A8;
    white-space: nowrap;
    padding: 2px 8px;
    background: #F7F8FA;
    border-radius: 10px;
    border: 1px solid #D8DCE3;
  }
</style>
